import { supabase } from "@/integrations/supabase/client";
import { CalendarConnectionManager } from "./connection-manager";
import { ProactiveTokenManager } from "./proactive-token-manager";

export class CalendarSelfHealingManager {
  private static instance: CalendarSelfHealingManager;
  private healingInterval: NodeJS.Timeout | null = null;
  private readonly HEALING_INTERVAL = 2 * 60 * 1000; // Check every 2 minutes
  private isHealing = false;

  static getInstance(): CalendarSelfHealingManager {
    if (!this.instance) {
      this.instance = new CalendarSelfHealingManager();
    }
    return this.instance;
  }

  start(): void {
    if (this.healingInterval) {
      console.log('🔄 Calendar self-healing already running');
      return;
    }

    
    // Initial healing check after a short delay
    setTimeout(() => {
      this.performHealingCheck();
    }, 5000);
    
    // Set up periodic healing checks
    this.healingInterval = setInterval(() => {
      this.performHealingCheck();
    }, this.HEALING_INTERVAL);
  }

  stop(): void {
    if (this.healingInterval) {
      clearInterval(this.healingInterval);
      this.healingInterval = null;
      console.log('🛑 Stopped calendar self-healing manager');
    }
  }

  private async performHealingCheck(): Promise<void> {
    if (this.isHealing) {
      console.log('🔄 Healing already in progress, skipping...');
      return;
    }

    try {
      this.isHealing = true;

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return;
      }

      // Check calendar health
      const health = await CalendarConnectionManager.checkConnectionHealth(user.id);
      
      if (health.isHealthy) {
        
        // If healthy, reactivate any deactivated meeting links
        await this.reactivateMeetingLinksIfHealthy(user.id);
        return;
      }

      console.log('⚠️ Calendar connection needs healing:', health.error);
      
      // Attempt automatic healing
      await this.attemptHealing(user.id, health);
      
    } catch (error) {
      console.error('❌ Error during calendar self-healing:', error);
    } finally {
      this.isHealing = false;
    }
  }

  private async attemptHealing(userId: string, health: any): Promise<void> {
    try {
      console.log('🩹 Attempting to heal calendar connection...');

      // Step 1: Try to refresh tokens automatically
      const refreshResult = await ProactiveTokenManager.ensureValidToken(userId);
      
      if (refreshResult.success) {
        console.log('✅ Token refresh successful, connection healed');
        
        // Clear cache to get fresh health status
        CalendarConnectionManager.clearUserCache(userId);
        
        // Check health again
        const newHealth = await CalendarConnectionManager.checkConnectionHealth(userId);
        
        if (newHealth.isHealthy) {
          console.log('🎉 Calendar connection fully healed!');
          
          // Reactivate meeting links
          await this.reactivateMeetingLinksIfHealthy(userId);
          
          // Clear any error states
          await this.clearErrorStates(userId);
        }
        return;
      }

      console.log('⚠️ Token refresh failed, trying alternative healing methods...');

      // Step 2: If token refresh failed, try to fix database health status
      await this.fixDatabaseHealthStatus(userId);

      // Step 3: Check if we can still consider the connection healthy despite issues
      const updatedHealth = await CalendarConnectionManager.checkConnectionHealth(userId);
      
      if (updatedHealth.isHealthy) {
        console.log('🎉 Calendar connection healed through database fix!');
        await this.reactivateMeetingLinksIfHealthy(userId);
      } else {
        console.log('⚠️ Calendar connection still needs attention, but will retry automatically');
      }

    } catch (error) {
      console.error('❌ Error during healing attempt:', error);
    }
  }

  private async fixDatabaseHealthStatus(userId: string): Promise<void> {
    try {
      console.log('🔧 Attempting to fix database health status...');

      const { data: connection, error } = await supabase
        .from('calendar_connections')
        .select('*')
        .eq('user_id', userId)
        .eq('provider', 'google')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error || !connection) {
        console.log('❌ No connection found to fix');
        return;
      }

      // Reset health status to healthy if it's not invalid
      if (connection.token_health_status !== 'invalid') {
        const { error: updateError } = await supabase
          .from('calendar_connections')
          .update({
            token_health_status: 'healthy',
            updated_at: new Date().toISOString()
          })
          .eq('id', connection.id);

        if (!updateError) {
          console.log('✅ Database health status reset to healthy');
        } else {
          console.error('❌ Failed to reset health status:', updateError);
        }
      }

      // Reduce failure count if it's not too high
      if ((connection.refresh_failure_count || 0) > 0 && (connection.refresh_failure_count || 0) < 10) {
        const { error: updateError } = await supabase
          .from('calendar_connections')
          .update({
            refresh_failure_count: 0,
            updated_at: new Date().toISOString()
          })
          .eq('id', connection.id);

        if (!updateError) {
          console.log('✅ Reset failure count to 0');
        }
      }

    } catch (error) {
      console.error('❌ Error fixing database health status:', error);
    }
  }

  private async reactivateMeetingLinksIfHealthy(userId: string): Promise<void> {
    try {
      console.log('🔗 Checking if meeting links need reactivation...');

      const { data: deactivatedLinks, error } = await supabase
        .from('meeting_links')
        .select('id, name')
        .eq('user_id', userId)
        .eq('active', false);

      if (error) {
        console.error('❌ Error checking deactivated links:', error);
        return;
      }

      if (!deactivatedLinks || deactivatedLinks.length === 0) {
        console.log('✅ No deactivated meeting links found');
        return;
      }

      console.log(`🔄 Found ${deactivatedLinks.length} deactivated meeting links, reactivating...`);

      const { error: reactivateError } = await supabase
        .from('meeting_links')
        .update({
          active: true,
          deactivation_reason: null,
          deactivated_at: null
        })
        .eq('user_id', userId)
        .eq('active', false);

      if (reactivateError) {
        console.error('❌ Error reactivating meeting links:', reactivateError);
      } else {
        console.log(`✅ Successfully reactivated ${deactivatedLinks.length} meeting links`);
      }

    } catch (error) {
      console.error('❌ Error reactivating meeting links:', error);
    }
  }

  private async clearErrorStates(userId: string): Promise<void> {
    try {
      console.log('🧹 Clearing error states...');

      // Clear any error notifications or states
      // This could include clearing error banners, notifications, etc.
      
      console.log('✅ Error states cleared');

    } catch (error) {
      console.error('❌ Error clearing error states:', error);
    }
  }

  // Public method to force immediate healing
  async forceHeal(): Promise<void> {
    console.log('🚨 Force healing requested...');
    await this.performHealingCheck();
  }
}